import { Widget } from '@sprinklr/stories/widget/Widget';
import { Theme } from 'models/Theme/Theme';
import { CollapseProps, TunablesCollapse } from 'components/Sidebar/RenderTunables/RenderTunables';
import i18n from 'i18next';
import { headerTunables, opacityTunables } from 'components/Sidebar/RenderTunables/SharedTunables';
import {
    xAxisTunables,
    yAxisTunables,
    gridTunables,
    legendTunables,
    metaInfoTunables,
} from 'components/Sidebar/RenderTunables/HyperspaceSharedTunables';
import React from 'react';
import ActionCheckboxGroup from 'components/_UI/Forms/Actions/ActionCheckboxGroup';
import { QuadrantConfig } from './types';
import LabelGroup from 'components/_UI/Forms/LabelGroup/LabelGroup';
import ActionInput from 'components/_UI/Forms/Actions/ActionInput';
import ActionSliderGroup from 'components/_UI/Forms/Actions/ActionSliderGroup';
import { action, toJS } from 'mobx';
import { mergeThemes } from 'utils/GenerateStyles/GenerateStyles';
import ActionCheckboxSliderColorSwatchGroup from 'components/_UI/Forms/Actions/ActionCheckboxSliderColorSwatchGroup';
import ActionCheckboxSliderGroup from 'components/_UI/Forms/Actions/ActionCheckboxSliderGroup';

export const quadrantConfigTunables = (quadrantConfig: QuadrantConfig): TunablesCollapse => {
    return {
        props: {
            label: 'Quadrant Labels',
            startOpen: true,
            isEnabled: quadrantConfig.labels.enabled,
            option: quadrantConfig.labels,
            value: 'enabled',
        } as CollapseProps,
        children: [
            <LabelGroup key='widget.options.quadrantConfig.label.topLeft' label={i18n.t('topLeft')}>
                <ActionInput option={quadrantConfig.labels} value='topLeft' />
            </LabelGroup>,
            <LabelGroup
                key='widget.options.quadrantConfig.label.topright'
                label={i18n.t('topRight')}>
                <ActionInput option={quadrantConfig.labels} value='topRight' />
            </LabelGroup>,
            <LabelGroup
                key='widget.options.quadrantConfig.label.bottomLeft'
                label={i18n.t('bottomLeft')}>
                <ActionInput option={quadrantConfig.labels} value='bottomLeft' />
            </LabelGroup>,
            <LabelGroup
                key='widget.options.quadrantConfig.label.bottomRight'
                label={i18n.t('bottomRight')}>
                <ActionInput option={quadrantConfig.labels} value='bottomRight' />
            </LabelGroup>,
        ],
    };
};

const clearColor = action((object: object, key: string) => {
    object[key] = '';
});

const updateColor = action(({ rgb: { r, g, b, a = 1 } }, object: object, key: string) => {
    object[key] = `rgba(${r}, ${g}, ${b}, ${a})`;
});

export const quadrantSeparatorsTunables = ({
    quadrantConfig,
    theme,
}: {
    quadrantConfig: QuadrantConfig;
    theme: Theme;
}): TunablesCollapse => {
    return {
        props: {
            label: 'Quadrant Separators',
            startOpen: true,
            isEnabled: quadrantConfig.separators.enabled,
            option: quadrantConfig.separators,
            value: 'enabled',
        } as CollapseProps,
        children: [
            <ActionCheckboxSliderColorSwatchGroup
                key='line'
                label={i18n.t('Line')}
                showValue
                data-testid='options-quadrant-separators-line'
                checkbox={undefined}
                slider={{
                    min: 1,
                    max: 50,
                    value: 'size',
                    option: quadrantConfig.separators,
                }}
                colorSwatch={{
                    option: null,
                    value: null,
                    color: quadrantConfig.separators.color || theme?.typography?.color,
                    onClear: () => clearColor(quadrantConfig.separators, 'color'),
                    onChange: rgb => updateColor(rgb, quadrantConfig.separators, 'color'),
                    secondaryIcon:
                        quadrantConfig.separators.color &&
                        quadrantConfig.separators.color !== theme?.typography?.color
                            ? 'icon-undo'
                            : undefined,
                }}
            />,
        ],
    };
};

export const quadrantLabelsTunables = ({
    quadrantConfig,
}: {
    quadrantConfig: QuadrantConfig;
}): TunablesCollapse => {
    return {
        props: {
            label: 'Quadrant Labels',
            startOpen: true,
            isEnabled: quadrantConfig.labels.enabled,
            option: quadrantConfig.labels,
            value: 'enabled',
        } as CollapseProps,
        children: [
            <ActionSliderGroup
                key='size'
                label={i18n.t('Size')}
                showValue
                option={quadrantConfig.labels}
                value='size'
            />,
            <LabelGroup key='topLeft' label={i18n.t('Top Left')}>
                <ActionInput
                    placeholder='Enter Quadrant Label'
                    option={quadrantConfig.labels}
                    value='topLeft'
                />
            </LabelGroup>,
            <LabelGroup key='topright' label={i18n.t('Top Right')}>
                <ActionInput
                    placeholder='Enter Quadrant Label'
                    option={quadrantConfig.labels}
                    value='topRight'
                />
            </LabelGroup>,
            <LabelGroup key='bottomLeft' label={i18n.t('Bottom Left')}>
                <ActionInput
                    placeholder='Enter Quadrant Label'
                    option={quadrantConfig.labels}
                    value='bottomLeft'
                />
            </LabelGroup>,
            <LabelGroup key='bottomRight' label={i18n.t('Bottom Right')}>
                <ActionInput
                    placeholder='Enter Quadrant Label'
                    option={quadrantConfig.labels}
                    value='bottomRight'
                />
            </LabelGroup>,
        ],
    };
};

export const HyperSpaceQuadrantMatrixWidgetTunables = (
    widget: Widget,
    mergedTheme: Theme
): TunablesCollapse[] => [
    {
        props: {
            label: i18n.t('Quadrant Matrix'),
            startOpen: true,
        } as CollapseProps,
        children: [
            ...headerTunables(widget),
            opacityTunables(widget),
            <ActionCheckboxGroup
                key='widget.options.zeroFill'
                label={i18n.t('Zero Fill')}
                option={widget.options}
                value='zeroFill'
            />,
            gridTunables(widget),
            <ActionCheckboxSliderGroup
                key='data-labels'
                label={i18n.t('Data Labels')}
                slider={{
                    value: 'fontSize',
                    min: 0,
                    max: 100,
                    option: widget.options.baseXYChartOptions?.dataLabels,
                }}
                checkbox={{
                    checked: true,
                    option: widget.options.baseXYChartOptions?.dataLabels,
                    value: 'enabled',
                }}
                showValue
            />,
            <ActionSliderGroup
                key='opacity'
                label={i18n.t('Bubble Opacity')}
                option={widget.options.bubble}
                value='opacity'
                showValue
                min={0}
                max={100}
            />,
            <ActionSliderGroup
                key='min'
                label={i18n.t('Radius Min')}
                option={widget.options.bubble.radius}
                value='min'
                showValue
                min={0}
                max={75}
            />,
            <ActionSliderGroup
                key='max'
                label={i18n.t('Radius Max')}
                option={widget.options.bubble.radius}
                value='max'
                showValue
                min={widget.options.bubble.radius.min}
                max={100}
            />,
        ],
    },
    quadrantLabelsTunables({ quadrantConfig: widget.options.quadrantConfig }),
    quadrantSeparatorsTunables({
        quadrantConfig: widget.options.quadrantConfig,
        theme: mergeThemes(toJS(mergedTheme), toJS(widget.theme)),
    }),

    metaInfoTunables({ widget, color: true, image: true }),
    xAxisTunables({ widget, mergedTheme }),
    yAxisTunables({ widget, mergedTheme, axisIsDim: false }),
    legendTunables(widget, { metric: false }),
];
